import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

export default function LifeCraftPrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-950 text-gray-200">
      <nav className="border-b border-purple-500/20 bg-slate-950/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">OmniAlera</span>
          </Link>
        </div>
      </nav>

      <motion.div
        className="max-w-4xl mx-auto px-6 pt-16 pb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-5xl md:text-6xl font-bold mb-4">
          <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            LifeCraft Privacy Policy
          </span>
        </h1>
        <p className="text-gray-400 text-lg">Last updated: May 8, 2026</p>
      </motion.div>

      <article className="max-w-4xl mx-auto px-6 pb-24 text-gray-300 leading-relaxed">
        <p className="mb-4 p-4 border border-purple-500/30 bg-purple-500/5 rounded-lg">
          <strong className="text-white">Scope:</strong> This Privacy Policy covers the LifeCraft mobile game and its associated web checkout experience on omnialera.com. OmniAlera Technologies Holdings Inc. operates multiple products across healthcare, consumer apps, and gaming. Each product maintains its own legal pages.{' '}
          <Link to="/" className="text-purple-300 hover:text-purple-200 underline">View OmniAlera homepage</Link>{' '}for links to other products' policies.
        </p>
        <p className="mb-4">
          OmniAlera Technologies Holdings Inc. ("OmniAlera," "we," "us," or "our") respects your privacy and is committed to protecting it. This Privacy Policy explains how we collect, use, share, and protect information when you use our products and services, including LifeCraft and our other consumer applications.
        </p>
        <p className="mb-4">
          By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use our services.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-4">1. Information We Collect</h2>

        <h3 className="text-xl font-semibold text-purple-200 mt-8 mb-3">1.1 Account and Identification Information</h3>
        <p className="mb-4">
          We use Firebase Anonymous Authentication to identify users. This generates a pseudonymous user identifier (UID) without requiring you to provide an email address, phone number, or other personal information. We do not collect your name, email, or contact details unless you voluntarily provide them (for example, by emailing our support address).
        </p>

        <h3 className="text-xl font-semibold text-purple-200 mt-8 mb-3">1.2 Game Data</h3>
        <p className="mb-4">When you use LifeCraft, we collect:</p>
        <ul className="list-disc list-inside mb-4 space-y-2 ml-4">
          <li>A player name you choose (you may use a pseudonym)</li>
          <li>The cultural context you select for your simulated life</li>
          <li>Decisions you make during gameplay</li>
          <li>Attribute changes resulting from your decisions</li>
          <li>NPC relationship state you build during a life</li>
          <li>Chapter progression and age within the simulation</li>
          <li>Save metadata (creation date, last played date)</li>
        </ul>
        <p className="mb-4">
          This data is associated with your Firebase UID, not with your real-world identity.
        </p>

        <h3 className="text-xl font-semibold text-purple-200 mt-8 mb-3">1.3 Payment Information</h3>
        <p className="mb-4">
          When you make a purchase (currently $6.99 per-life unlock for LifeCraft), payment is processed by Stripe, Inc. We do not collect, store, or have access to your full credit card or payment account details. Stripe shares with us a unique payment session identifier, transaction status (completed, failed, refunded), the email address you provide at checkout (used only for receipt and customer support), and country of payment for tax compliance.
        </p>
        <p className="mb-4">
          Stripe's own privacy policy governs how Stripe handles your payment information:{' '}
          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-300 hover:text-purple-200 underline">
            stripe.com/privacy
          </a>
          .
        </p>

        <h3 className="text-xl font-semibold text-purple-200 mt-8 mb-3">1.4 AI-Generated Narrative Data</h3>
        <p className="mb-4">
          LifeCraft uses Anthropic's Claude API to generate narrative content based on your decisions. We send Anthropic your decisions and the resulting attribute deltas, your selected cultural context, your player name (if user-provided), and a summary of your save state. We do not send Anthropic any directly identifying information.
        </p>

        <h3 className="text-xl font-semibold text-purple-200 mt-8 mb-3">1.5 Voice Interaction Data</h3>
        <p className="mb-4">
          LifeCraft uses ElevenLabs (primary) and OpenAI (fallback) for text-to-speech narration. Narrative text is sent to these providers for audio synthesis. The text contains your culturally-contextualized narrative — it does not contain payment information or directly identifying personal data.
        </p>

        <h3 className="text-xl font-semibold text-purple-200 mt-8 mb-3">1.6 Technical and Usage Data</h3>
        <p className="mb-4">We automatically collect IP address (used for rate limiting and abuse prevention), device type and operating system, app version, anonymized error reports, and aggregated usage analytics (e.g., feature adoption, session frequency).</p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-4">2. How We Use Information</h2>
        <p className="mb-4">We use the information we collect to:</p>
        <ul className="list-disc list-inside mb-4 space-y-2 ml-4">
          <li>Operate and maintain our services</li>
          <li>Personalize your experience (e.g., culturally-contextualized narratives)</li>
          <li>Process payments and unlock paid content</li>
          <li>Detect and prevent fraud, abuse, or security incidents</li>
          <li>Improve our products through aggregated, de-identified analysis</li>
          <li>Comply with legal obligations</li>
          <li>Communicate with you when you contact us for support</li>
        </ul>
        <p className="mb-4 font-semibold text-white">We do not sell your personal information.</p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-4">3. Third-Party Service Providers</h2>
        <p className="mb-4">
          We rely on the following service providers to operate our services. Each operates under their own privacy policy:
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full border-collapse border border-purple-500/20">
            <thead>
              <tr className="bg-purple-500/10">
                <th className="border border-purple-500/20 px-4 py-2 text-left text-white">Provider</th>
                <th className="border border-purple-500/20 px-4 py-2 text-left text-white">Purpose</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="border border-purple-500/20 px-4 py-2">Firebase (Google LLC)</td><td className="border border-purple-500/20 px-4 py-2">Anonymous authentication</td></tr>
              <tr><td className="border border-purple-500/20 px-4 py-2">Neon, Inc.</td><td className="border border-purple-500/20 px-4 py-2">Database hosting</td></tr>
              <tr><td className="border border-purple-500/20 px-4 py-2">Render Services, Inc.</td><td className="border border-purple-500/20 px-4 py-2">Backend server hosting</td></tr>
              <tr><td className="border border-purple-500/20 px-4 py-2">Anthropic, PBC</td><td className="border border-purple-500/20 px-4 py-2">AI narrative generation</td></tr>
              <tr><td className="border border-purple-500/20 px-4 py-2">ElevenLabs, Inc.</td><td className="border border-purple-500/20 px-4 py-2">Voice synthesis (primary)</td></tr>
              <tr><td className="border border-purple-500/20 px-4 py-2">OpenAI, OpCo, LLC</td><td className="border border-purple-500/20 px-4 py-2">Voice synthesis (fallback)</td></tr>
              <tr><td className="border border-purple-500/20 px-4 py-2">Stripe, Inc.</td><td className="border border-purple-500/20 px-4 py-2">Payment processing</td></tr>
              <tr><td className="border border-purple-500/20 px-4 py-2">Netlify, Inc.</td><td className="border border-purple-500/20 px-4 py-2">Marketing website hosting</td></tr>
            </tbody>
          </table>
        </div>
        <p className="mb-4">We share information with these providers only to the extent necessary to operate our services.</p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-4">4. Data Retention</h2>
        <p className="mb-4">
          We retain your data for as long as your account is active. If you wish to delete your data, contact us at{' '}
          <a href="mailto:ceo@omnialera.com" className="text-purple-300 hover:text-purple-200 underline">ceo@omnialera.com</a>. Upon deletion, we will remove your save data and game progression from our database within 30 days, anonymize associated payment records (we are required to retain transaction records for tax compliance, but these will no longer be linked to your identity), and remove cached narratives and voice synthesis logs.
        </p>
        <p className="mb-4">Aggregated, de-identified data may be retained indefinitely for analytics purposes.</p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-4">5. Your Rights</h2>

        <h3 className="text-xl font-semibold text-purple-200 mt-8 mb-3">5.1 GDPR Rights (European Economic Area)</h3>
        <p className="mb-4">Under the General Data Protection Regulation, you have the right to access the personal data we hold about you, correct inaccurate or incomplete data, request deletion of your data, restrict or object to processing, receive your data in a portable format, and lodge a complaint with a supervisory authority.</p>

        <h3 className="text-xl font-semibold text-purple-200 mt-8 mb-3">5.2 CCPA Rights (California Residents)</h3>
        <p className="mb-4">Under the California Consumer Privacy Act, you have the right to know what personal information we collect, request deletion of your personal information, opt-out of the sale of personal information (note: we do not sell personal information), and non-discrimination for exercising your CCPA rights.</p>

        <p className="mb-4">
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:ceo@omnialera.com" className="text-purple-300 hover:text-purple-200 underline">ceo@omnialera.com</a>.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-4">6. International Data Transfers</h2>
        <p className="mb-4">
          Our service providers (including Anthropic, OpenAI, Stripe, Firebase, and others) operate primarily in the United States. By using our services, you consent to your data being transferred to and processed in the United States and other jurisdictions where our service providers operate. We rely on standard contractual clauses or other lawful transfer mechanisms where required.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-4">7. Security</h2>
        <p className="mb-4">We use industry-standard security measures to protect your data, including TLS encryption for all data in transit, encryption at rest in our databases, anonymous Firebase authentication (no passwords stored), regular security reviews, and limited employee access to user data on a need-to-know basis.</p>
        <p className="mb-4">No method of transmission or storage is 100% secure. We cannot guarantee absolute security.</p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-4">8. Children's Privacy</h2>
        <p className="mb-4">
          Our services are intended for users 18 years of age or older. We do not knowingly collect personal information from anyone under 18. If you believe we have collected information from a minor, please contact us immediately at{' '}
          <a href="mailto:ceo@omnialera.com" className="text-purple-300 hover:text-purple-200 underline">ceo@omnialera.com</a> and we will delete it.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-4">9. Changes to This Policy</h2>
        <p className="mb-4">We may update this Privacy Policy from time to time. Changes will be reflected in the "Last updated" date at the top of this page. For material changes, we will provide reasonable notice, including via in-app notification or email if applicable.</p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-4">10. Contact Us</h2>
        <p className="mb-4">If you have questions about this Privacy Policy or our data practices, contact us at:</p>
        <p className="mb-4">
          OmniAlera Technologies Holdings Inc.<br />
          Email:{' '}
          <a href="mailto:ceo@omnialera.com" className="text-purple-300 hover:text-purple-200 underline">ceo@omnialera.com</a>
          <br />
          Website:{' '}
          <a href="https://omnialera.com" className="text-purple-300 hover:text-purple-200 underline">omnialera.com</a>
        </p>
      </article>

      <footer className="border-t border-purple-500/20 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500 text-sm">
          © 2026 OmniAlera Technologies Holdings Inc. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
